import { Button, DatePicker, Input, Switch, Typography, message } from 'antd'
import React from 'react'
import dayjs from 'dayjs'
import { sellerApi } from 'api/seller'
import { ICommissionDiscount } from 'models/seller'
import { showServerError } from 'shared/utils/showServerError'
import { normalizeNumber } from 'shared/utils/normalizeNumber'
import styles from './SellerCommissionDiscount.module.scss'

interface ISellerCommissionDiscountProps {
  sellerId: string
  currentCommission: number
  discount?: ICommissionDiscount
  style?: React.CSSProperties
  onSuccess: (discount: ICommissionDiscount) => void
}

export const SellerCommissionDiscount: React.FC<ISellerCommissionDiscountProps> = ({
  sellerId,
  currentCommission,
  discount,
  style,
  onSuccess
}) => {
  const [isActive, setIsActive] = React.useState(Boolean(discount?.is_active))
  const [rate, setRate] = React.useState(
    discount?.rate != null ? String(discount.rate) : ''
  )
  const [endDate, setEndDate] = React.useState<dayjs.Dayjs | null>(
    discount?.end_date ? dayjs(discount.end_date) : null
  )
  const [isLoading, setIsLoading] = React.useState(false)

  // База — сохранённое состояние акции; кнопку «Сохранить» показываем только при отличии от неё
  const baselineActive = Boolean(discount?.is_active)
  const baselineRate = discount?.rate != null ? String(discount.rate) : ''
  const baselineEndDate = discount?.end_date
    ? dayjs(discount.end_date).format('YYYY-MM-DD')
    : ''
  const currentEndDate = endDate ? endDate.format('YYYY-MM-DD') : ''
  const hasChanges =
    isActive !== baselineActive ||
    (isActive &&
      (rate.trim() !== baselineRate || currentEndDate !== baselineEndDate))

  const onSubmit = async () => {
    const rateNumber = normalizeNumber(rate)
    if (isActive) {
      if (Number.isNaN(rateNumber) || rateNumber < 0) {
        return message.error('Укажите корректный размер сниженной комиссии')
      }
      if (rateNumber >= currentCommission) {
        return message.error('Сниженная комиссия должна быть меньше текущей')
      }
      if (!endDate) {
        return message.error('Укажите дату окончания акции')
      }
    }
    try {
      setIsLoading(true)
      const response = await sellerApi.changeCommissionDiscount(
        sellerId,
        isActive
          ? {
            is_active: true,
            rate: rateNumber,
            end_date: endDate!.endOf('day').toISOString()
          }
          : { is_active: false }
      )
      onSuccess(response.data.commission_discount)
      message.success(
        isActive ? 'Сниженная комиссия применена' : 'Сниженная комиссия отключена'
      )
    } catch (e) {
      showServerError(e)
    } finally {
      setIsLoading(false)
    }
  }

  return (
    <div style={style}>
      <div className={styles.header}>
        <div className={styles.titleRow}>
          <Typography.Title level={3} className={styles.subTitle}>
            Снизить на время
          </Typography.Title>
          <Switch checked={isActive} onChange={setIsActive} />
        </div>
        {isActive && (
          <>
            <Input
              className={styles.input}
              value={rate}
              onChange={(e) => setRate(e.target.value)}
              placeholder='Комиссия %'
            />
            <DatePicker
              className={styles.datePicker}
              value={endDate}
              onChange={(date) => setEndDate(date)}
              format='DD/MM/YYYY'
              placeholder='Дата окончания'
              disabledDate={(current) => current && current < dayjs().startOf('day')}
              inputReadOnly
            />
          </>
        )}
        <div className={styles.footer}>
          {hasChanges && (
            <Button
              htmlType='button'
              type='primary'
              loading={isLoading}
              onClick={onSubmit}
            >
              Сохранить
            </Button>
          )}
        </div>
      </div>
    </div>
  )
}
